import { Button, Stack, Typography } from '@mui/material'
import { EquipmentPlannedDto, StoppageResponseDto } from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'
import { useTranslate } from '@tolgee/react'
import { ContainerMobile, Header, IconButton, MenuIcon, useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { VesselTallyStore } from '../../models/vessel-tally-store.model'
import { StoppageCard } from '../Molecules/StoppageCard'

interface IProps {
  vesselVisitId: number
  selectedCrane: EquipmentPlannedDto
  visitName?: string
  openDrawer: () => void
  onRecordStoppage: (stoppage?: StoppageResponseDto) => void
  operationStoppedComponent?: React.ReactNode
  vesselTallyStore: VesselTallyStore
}
export const Stoppages = observer(
  ({
    vesselVisitId,
    selectedCrane,
    visitName,
    openDrawer,
    onRecordStoppage,
    operationStoppedComponent,
    vesselTallyStore,
  }: IProps) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { stoppageStore, stoppageReasonStore } = useOperationsStore()

    useAsyncFetch(async () => {
      await Promise.all([
        stoppageStore.fetchDataByVisitId(vesselVisitId),
        stoppageStore.loadEquipments(vesselVisitId),
        stoppageReasonStore.fetchStoppageReasons(),
      ])

      stoppageStore.setSelectedVisitId(vesselVisitId)
    }, [stoppageReasonStore, stoppageStore, vesselVisitId])

    const openStoppages = computed(() =>
      stoppageStore.orderedItems.filter(
        x => !x.to && (!x.equipments.length || x.equipments.some(eq => eq.id === selectedCrane.id)),
      ),
    ).get()

    return (
      <>
        <Header
          title={t('openStoppages', 'Open stoppages')}
          subTitle={`${visitName ?? t('selectedCrane', 'Selected crane')} - ${selectedCrane.name}`}
          leftRenderOption={
            vesselTallyStore.isSelectedVisitInOperation ? (
              <IconButton onClick={openDrawer}>
                <MenuIcon />
              </IconButton>
            ) : undefined
          }
          rightRenderOption={
            <Button
              variant='contained'
              data-cy='record-new-stoppage-btn'
              onClick={() => onRecordStoppage()}
              disabled={!vesselTallyStore.isSelectedVisitInOperation}
            >
              {t('record', 'Record')}
            </Button>
          }
        />

        <ContainerMobile sx={{ pt: theme.customSpacing.l }}>
          <Stack gap={theme.customSpacing.m}>
            {openStoppages.map(stoppage => (
              <StoppageCard key={stoppage.id} stoppage={stoppage} onEdit={onRecordStoppage} />
            ))}

            {openStoppages.length === 0 && (
              <Typography variant='body1'>
                {t('noStoppagesRecord', 'No stoppages recorded')}
              </Typography>
            )}
            {operationStoppedComponent}
          </Stack>
        </ContainerMobile>
      </>
    )
  },
)
